<script setup lang="ts">
import type { FieldValidator } from 'vee-validate';
import { useField } from 'vee-validate';
import { onMounted, useSlots, watch } from 'vue';

const slots = useSlots();

interface Props {
  modelValue: string | number | null
  inputId: string
  type: string
  name: string
  step?: string
  min?: number
  max?: number
  labelText?: string | boolean
  required?: boolean
  placeholder?: string
  width?: string
  disabled?: boolean
  subject?: boolean
  accept?: string
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  placeholder: '',
  width: '100%',
  disabled: false,
  subject: false
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number): void,
  (e: 'validationError', value: boolean): void,
  (e: 'validationFn', value: FieldValidator): void,
  (e: 'onEnter', value: string): void,
}>();

const { value, errorMessage, validate, meta } = useField<any>(() => props.name, props.rules, {
  syncVModel: true
});

onMounted(() => {
  emit('validationError', !!meta.valid);
  emit('validationFn', validate);
});

watch(meta, () => {
  emit('validationError', !!meta.valid);
});
</script>

<template>
  <div
    :style="`width: ${width}`"
    class="input-base"
  >
    <label
      v-if="labelText"
      :for="inputId"
      :class="[
        required ? 'input-base__label--required' : '',
        errorMessage ? 'input-base__label--not-valid' : '',
      ]"
      class="input-base__label text-dark"
    >
      {{ labelText }}
    </label>
    <div class="input-base__wrapper">
      <div
        v-if="slots.icon"
        class="input-base__icon"
      >
        <slot
          name="icon"
          class="input-base__icon"
        />
      </div>

      <input
        :id="inputId"
        v-model="value"
        :type="type"
        :placeholder="placeholder"
        :class="[
          disabled ? 'input-base__input--disabled' : '',
          errorMessage ? 'input-base__input--not-valid' : '',
          slots.icon ? 'input-base__input--with-icon' : '',
          subject ? 'input-base__input--subject' : '',
          labelText ? 'input-base__input--with-text' : '',
        ]"
        :disabled="disabled"
        :step="step"
        :min="min"
        :max="max"
        :accept="accept"
        class="input-base__input"
        @keyup.enter="$emit('onEnter', value)"
      >
    </div>

    <div
      v-if="errorMessage"
      class="text-error"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.input-base {
  &__label {
    display: block;
    position: relative;
    font-size: 14px;
    font-weight: 700;
    margin-bottom: 6px;

    &--required {
      &::after {
        content: '*';
        color: var(--red);
        display: inline-block;
        margin-right: -2px;
      }
    }

    &--not-valid {
      color: var(--red);
    }
  }

  &__wrapper {
    position: relative;
  }

  &__icon {
    position: absolute;
    top: 50%;
    left: 8px;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    width: 20px;
    height: 20px;
  }

  &__input {
    padding: 9px 15px;
    border-radius: 2px;
    border: none;
    background-color: var(--white);
    width: 100%;
    font-size: 14px;
    height: 40px;

    &::placeholder {
      font-size: var(--placeholder-font-size);
      color: var(--placeholder-color);
      font-weight: 400;
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
    }

    &:focus-visible {
      outline: none;
    }

    &--with-text {
      border: 1px solid var(--border-default);
    }

    &--subject {
      font-size: 20px;

      &::placeholder {
        font-size: 20px;
      }
    }

    &--disabled {
      background-image: none;
      background-color: var(--gray);
      color: var(--gray-darkest);
    }

    &--not-valid {
      border-color: var(--red);

      &::placeholder {
        color: (--red);
        font-weight: 400;
      }
    }

    &--with-icon {
      padding-left: 36px;
      padding-right: 36px;
    }
  }
}

.text-error {
  font-size: 12px;
  line-height: 1.5;
  color: var(--red);
  position: absolute;
  left: 0;
  white-space: nowrap;
}
</style>
